import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from '../shared/AnimatedSection';
const PREVIEW_video = 'https://cdn.shopify.com/videos/c/o/v/f360373347134c5c908f3eff7067ffd9.mp4';
const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8D48B';
interface Benefit {
  title: string;
  desc: string;
}
const BENEFITS: Benefit[] = [{
  title: 'Comprender por qué reaccionas de cierta manera',
  desc: 'Descubre patrones emocionales, formas de defensa y comportamientos que suelen repetirse en tus relaciones, decisiones y conflictos internos.'
}, {
  title: 'Identificar partes de ti que normalmente ignoras',
  desc: 'Explora aspectos de tu personalidad, emociones y dinámicas internas desde una nueva perspectiva más consciente y profunda.'
}, {
  title: 'Acceder a una experiencia inmersiva de autoconocimiento',
  desc: 'No solo leerás contenido. Entrarás en un universo diseñado para conectar contigo a través de simbolismo, identidad y exploración emocional.'
}, {
  title: 'Explorar tu Reino interior desde una nueva perspectiva',
  desc: 'Cada documento y experiencia fue creado para ayudarte a observarte con más claridad, profundidad y consciencia.'
}, {
  title: 'Acceso privado desde cualquier lugar',
  desc: 'Tu Portal de Usuario quedará disponible para ingresar cuando quieras y continuar tu experiencia dentro del Reino.'
}, {
  title: 'Una experiencia diseñada para sentirse personal',
  desc: 'Cada Reino posee una identidad visual, narrativa y emocional única, diseñada para que la experiencia se sienta inmersiva y diferente a una lectura tradicional.'
}];
export function DesbloqueoSection() {
  return <section className="relative w-full py-24 md:py-32 px-5 md:px-6 overflow-hidden bg-cosmic-black pt-[9px] pb-[9px]" data-id="element-112">
      {/* Atmospheric backdrop */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-113">
        <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse at 30% 30%, ${GOLD}12 0%, transparent 60%)`
      }} data-id="element-114" />
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, #050508 0%, rgba(5,5,8,0.4) 50%, #050508 100%)'
      }} data-id="element-115" />
      </div>

      {/* Floating embers */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-116">
        {[...Array(8)].map((_, i) => <motion.div key={i} className="absolute w-[2px] h-[2px] rounded-full" style={{
        backgroundColor: i % 2 === 0 ? GOLD : GOLD_LIGHT,
        left: `${i * 13 % 100}%`,
        top: `${15 + i * 11 % 70}%`,
        boxShadow: `0 0 8px ${i % 2 === 0 ? GOLD : GOLD_LIGHT}`
      }} animate={{
        y: [-10, -40, -10],
        opacity: [0.15, 0.5, 0.15]
      }} transition={{
        duration: 6 + i % 4,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.4
      }} data-id="element-117" />)}
      </div>

      <div className="relative z-10 container mx-auto max-w-6xl" data-id="element-118">
        {/* Header */}
        <AnimatedSection data-id="element-119">
          <div className="text-center mb-12 md:mb-16 px-2" data-id="element-120">
            <motion.div className="inline-flex items-center gap-3 mb-6" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-121">
              <div className="h-px w-10 md:w-12 bg-gradient-to-r from-transparent to-gold-primary" data-id="element-122" />
              <span className="md:text-[11px] tracking-[0.35em] uppercase font-serif text-[14px]" style={{
              color: GOLD_LIGHT
            }} data-id="element-123">
                Tu experiencia dentro del Reino
              </span>
              <div className="h-px w-10 md:w-12 bg-gradient-to-r from-gold-primary to-transparent" data-id="element-124" />
            </motion.div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-5 md:mb-6 leading-tight max-w-4xl mx-auto" style={{
            textShadow: `0 0 30px ${GOLD}30, 0 4px 30px rgba(0,0,0,0.7)`
          }} data-id="element-125">
              Lo que desbloquearás dentro de tu Reino
            </h2>

            <p className="max-w-2xl mx-auto text-base md:text-lg lg:text-xl italic leading-relaxed px-2 font-['DM_Sans',_sans-serif]" style={{
            color: 'rgba(203,185,139,0.95)'
          }} data-id="element-126">
              Esto no es solo una lectura.
              <br data-id="element-127" />
              Es una experiencia diseñada para ayudarte a comprender patrones
              internos que muchas veces repites sin darte cuenta.
            </p>
          </div>
        </AnimatedSection>

        {/* Video — larger on desktop, full-width on mobile */}
        <AnimatedSection delay={0.15} data-id="element-128">
          <motion.div className="relative w-full flex justify-center mb-12 md:mb-16 lg:mb-20" whileHover={{
          y: -4
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 22
        }} data-id="element-129">
            {/* Glow halo behind the video */}
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 mx-auto pointer-events-none" style={{
            width: '85%',
            height: '75%',
            background: `radial-gradient(ellipse at center, ${GOLD_LIGHT}28 0%, transparent 70%)`,
            filter: 'blur(60px)'
          }} data-id="element-130" />

            <video src={PREVIEW_video} autoPlay muted loop playsInline preload="metadata" aria-label="Vista previa del Portal del Reino — experiencia inmersiva de autoconocimiento" className="relative w-full max-w-md md:max-w-3xl lg:max-w-5xl rounded-2xl md:rounded-3xl" style={{
            border: `1px solid ${GOLD_LIGHT}26`,
            boxShadow: `0 0 80px ${GOLD_LIGHT}1A, 0 40px 100px rgba(0,0,0,0.7)`
          }} data-id="element-131" />
          </motion.div>
        </AnimatedSection>

        {/* Benefits — 2-col grid on desktop, stacked on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6 max-w-5xl mx-auto" data-id="element-132">
          {BENEFITS.map((benefit, i) => <motion.div key={benefit.title} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          delay: 0.05 + i * 0.06,
          duration: 0.55,
          ease: 'easeOut'
        }} className="relative rounded-xl px-5 py-4 md:px-6 md:py-5 backdrop-blur-sm" style={{
          background: 'linear-gradient(135deg, rgba(12,12,20,0.7) 0%, rgba(8,8,14,0.65) 100%)',
          border: `1px solid ${GOLD_LIGHT}22`,
          boxShadow: `0 0 22px rgba(201,168,76,0.06), inset 0 0 18px rgba(201,168,76,0.04)`
        }} data-id="element-133">
              <div className="flex items-start gap-3 md:gap-4" data-id="element-134">
                {/* Gold checkmark */}
                <span className="flex-shrink-0 mt-0.5 text-lg md:text-xl font-bold leading-none" style={{
              color: GOLD,
              textShadow: `0 0 10px ${GOLD}90`
            }} aria-hidden="true" data-id="element-135">
                  ✔
                </span>

                <div className="min-w-0" data-id="element-136">
                  <h3 className="text-base md:text-lg font-serif font-semibold leading-tight mb-1.5 md:mb-2" style={{
                color: '#F5F0E1'
              }} data-id="element-137">
                    {benefit.title}
                  </h3>
                  <p className="text-sm md:text-[15px] leading-relaxed font-['DM_Sans',_sans-serif] text-[#E9DA7A]" data-id="element-138">
                    {benefit.desc}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Closing paragraph */}
        <AnimatedSection delay={0.25} data-id="element-139">
          <div className="mt-14 md:mt-20 text-center px-2" data-id="element-140">
            <div className="flex items-center justify-center mb-6" data-id="element-141">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#d4af37]/50" data-id="element-142" />
              <div className="mx-4 w-1.5 h-1.5 rotate-45" style={{
              backgroundColor: GOLD,
              boxShadow: `0 0 8px ${GOLD}`
            }} data-id="element-143" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#d4af37]/50" data-id="element-144" />
            </div>

            <p className="max-w-2xl mx-auto text-base md:text-lg lg:text-xl font-serif italic leading-relaxed" style={{
            color: '#F5F0E1',
            textShadow: `0 0 18px ${GOLD}30`
          }} data-id="element-145">
              Todo dentro de una experiencia privada diseñada para profundizar
              en tu Reino emocional.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}